import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { HiChevronDown, HiArrowRightOnRectangle, HiBell, HiInbox } from 'react-icons/hi2';
import { onAuthStateChanged } from 'firebase/auth';
import { auth, firestore } from '../../../database/firebase';
import { doc, getDoc, collection, query, onSnapshot, serverTimestamp } from 'firebase/firestore';
import { useAuth } from '../../../context/AuthContext';
import LogoutConfirmation from '../../../components/ui/LogoutConfirmation';

const dropdownVariants = {
  hidden: { opacity: 0, y: -10, scale: 0.98 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { type: 'spring', stiffness: 200, damping: 18, duration: 0.22 },
  },
  exit: { opacity: 0, y: -10, scale: 0.98, transition: { duration: 0.15 } },
};

const AdminHeader = () => {
  const { logout } = useAuth();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [inboxOpen, setInboxOpen] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notifications, setNotifications] = useState([]);
  const [messages, setMessages] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const dropdownRef = useRef(null);
  const notificationsRef = useRef(null);
  const inboxRef = useRef(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async currentUser => {
      setLoading(true);
      if (currentUser) {
        setUser(currentUser);
        try {
          const userDocRef = doc(firestore, 'admin', currentUser.uid);
          const userDocSnap = await getDoc(userDocRef);
          if (userDocSnap.exists()) {
            setUserProfile(userDocSnap.data());
          } else {
            setUserProfile(null);
          }
        } catch (error) {
          setUserProfile(null);
        }
      } else {
        setUser(null);
        setUserProfile(null);
      }
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  // Task notifications - Admins see all new tasks
  useEffect(() => {
    if (!user?.email) return;

    const tasksQuery = query(collection(firestore, 'tasks'));

    const unsubscribeTasks = onSnapshot(
      tasksQuery,
      snapshot => {
        snapshot.docChanges().forEach(change => {
          if (change.type === 'added') {
            const taskData = change.doc.data();
            // Only notify for tasks created in the last hour (to avoid old tasks)
            const taskTime = taskData.createdAt?.toDate?.() || new Date();
            const oneHourAgo = new Date(Date.now() - 60 * 60 * 1000);

            if (taskTime > oneHourAgo) {
              setNotifications(prev => {
                if (prev.some(notification => notification.id === `task-${change.doc.id}`))
                  return prev;
                return [
                  ...prev,
                  {
                    id: `task-${change.doc.id}`,
                    type: 'task',
                    message: `New task: ${taskData.title || 'Untitled Task'} (Created by: ${taskData.createdByName || taskData.createdBy || 'Unknown'})`,
                    timestamp: taskData.createdAt || serverTimestamp(),
                    read: false,
                    taskId: change.doc.id,
                  },
                ];
              });
            }
          }
        });
      },
      error => {
        console.error('Task notification listener error:', error);
      }
    );

    return () => unsubscribeTasks();
  }, [user?.email]);

  useEffect(() => {
    setUnreadCount(notifications.filter(n => !n.read).length);
  }, [notifications]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
      if (notificationsRef.current && !notificationsRef.current.contains(event.target)) {
        setNotificationsOpen(false);
      }
      if (inboxRef.current && !inboxRef.current.contains(event.target)) {
        setInboxOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = async () => {
    setShowLogoutConfirm(true);
  };

  const handleInboxClick = () => {
    setInboxOpen(!inboxOpen);
  };

  const handleNotificationsClick = () => {
    setNotificationsOpen(!notificationsOpen);
  };
  const confirmLogout = async () => {
    setShowLogoutConfirm(false);
    await logout();
  };

  if (loading) {
    return (
      <motion.header
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="sticky top-0 z-40 flex w-full items-center justify-between border-b border-gray-200 bg-white/80 px-6 py-4 backdrop-blur-xl"
      >
        <div className="flex items-center gap-4">
          <motion.div
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.1 }}
          >
            <h1 className="text-xl font-bold text-black">
              <span className="ml-2 text-xs font-normal text-gray-500">Admin</span>
            </h1>
          </motion.div>
        </div>
        <div className="flex items-center gap-3">
          <div className="h-8 w-8 animate-pulse rounded-full bg-gray-200"></div>
          <div className="hidden flex-col gap-1 sm:flex">
            <div className="h-3 w-20 animate-pulse rounded bg-gray-200"></div>
            <div className="h-2 w-16 animate-pulse rounded bg-gray-200"></div>
          </div>
        </div>
      </motion.header>
    );
  }

  if (!user) return null;

  const displayName =
    userProfile?.firstname && userProfile?.lastname
      ? `${userProfile.firstname} ${userProfile.lastname}`
      : user.displayName || 'Admin';
  const firstName = userProfile?.firstname || user.displayName?.split(' ')[0] || 'Admin';
  const lastName = userProfile?.lastname || user.displayName?.split(' ')[1] || '';
  const initials = `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();

  return (
    <motion.header
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      className="sticky top-0 z-40 flex w-full items-center justify-between border-b border-gray-200 bg-white/80 px-4 py-3 backdrop-blur-xl md:px-6 md:py-4"
    >
      <div className="flex items-center gap-3 md:gap-4">
        <motion.div
          initial={{ opacity: 0, x: -10 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.1 }}
        >
          <h1 className="text-lg font-bold text-black md:text-xl">
            <span className="ml-1 text-[10px] font-normal text-gray-500 md:ml-2 md:text-xs">
              Admin
            </span>
          </h1>
        </motion.div>
      </div>
      <div className="flex items-center gap-2 md:gap-4">
        <div className="relative" ref={inboxRef}>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setInboxOpen(!inboxOpen)}
            className="relative rounded-xl p-1.5 text-gray-500 transition-colors hover:bg-gray-50 hover:text-gray-700 md:p-2"
          >
            <HiInbox className="h-4 w-4 md:h-5 md:w-5" />
            {unreadMessages > 0 && (
              <span className="absolute -right-0.5 -top-0.5 flex h-3 w-3 items-center justify-center rounded-full bg-blue-600 text-[10px] text-white md:-right-1 md:-top-1 md:h-4 md:w-4 md:text-xs">
                {unreadMessages}
              </span>
            )}
          </motion.button>
          <AnimatePresence>
            {inboxOpen && (
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: -10 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: -10 }}
                transition={{ duration: 0.15, ease: 'easeOut' }}
                className="absolute right-0 mt-2 w-72 rounded-2xl bg-white shadow-xl ring-1 ring-black/5 backdrop-blur-xl md:w-80"
              >
                <div className="border-b border-gray-100 px-3 py-2 md:px-4 md:py-3">
                  <h3 className="text-xs font-semibold text-black md:text-sm">Inbox</h3>
                </div>
                <div className="max-h-80 overflow-y-auto py-2 md:max-h-96">
                  {messages.length > 0 ? (
                    messages.map(message => (
                      <motion.div
                        key={message.id}
                        whileHover={{ backgroundColor: 'rgb(248 250 252)' }}
                        className={`flex items-start gap-2 px-3 py-2 md:gap-3 md:px-4 md:py-3 ${
                          !message.read ? 'bg-gray-50' : ''
                        }`}
                      >
                        <div className="flex-1">
                          <p className="text-xs text-black md:text-sm">{message.subject}</p>
                          <p className="mt-1 text-[10px] text-gray-500 md:text-xs">
                            {new Date(message.timestamp).toLocaleString()}
                          </p>
                        </div>
                      </motion.div>
                    ))
                  ) : (
                    <div className="px-3 py-2 text-center text-xs text-gray-500 md:px-4 md:py-3 md:text-sm">
                      No messages
                    </div>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        <div className="relative" ref={notificationsRef}>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setNotificationsOpen(!notificationsOpen)}
            className="relative rounded-xl p-1.5 text-gray-500 transition-colors hover:bg-gray-50 hover:text-gray-700 md:p-2"
          >
            <HiBell className="h-4 w-4 md:h-5 md:w-5" />
            {unreadCount > 0 && (
              <span className="absolute -right-0.5 -top-0.5 flex h-3 w-3 items-center justify-center rounded-full bg-blue-600 text-[10px] text-white md:-right-1 md:-top-1 md:h-4 md:w-4 md:text-xs">
                {unreadCount}
              </span>
            )}
          </motion.button>
          <AnimatePresence>
            {notificationsOpen && (
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: -10 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: -10 }}
                transition={{ duration: 0.15, ease: 'easeOut' }}
                className="absolute right-0 mt-2 w-72 rounded-2xl bg-white shadow-xl ring-1 ring-black/5 backdrop-blur-xl md:w-80"
              >
                <div className="border-b border-gray-100 px-3 py-2 md:px-4 md:py-3">
                  <h3 className="text-xs font-semibold text-black md:text-sm">Notifications</h3>
                </div>
                <div className="max-h-80 overflow-y-auto py-2 md:max-h-96">
                  {notifications.length > 0 ? (
                    notifications.map(notification => (
                      <motion.div
                        key={notification.id}
                        whileHover={{ backgroundColor: 'rgb(248 250 252)' }}
                        className={`flex items-start gap-2 px-3 py-2 md:gap-3 md:px-4 md:py-3 ${
                          !notification.read ? 'bg-gray-50' : ''
                        }`}
                      >
                        <div className="flex-1">
                          <p className="text-xs text-black md:text-sm">{notification.message}</p>
                          <p className="mt-1 text-[10px] text-gray-500 md:text-xs">
                            {notification.timestamp?.toDate
                              ? new Date(notification.timestamp.toDate()).toLocaleString()
                              : notification.timestamp
                                ? new Date(notification.timestamp).toLocaleString()
                                : 'Just now'}
                          </p>
                        </div>
                      </motion.div>
                    ))
                  ) : (
                    <div className="px-3 py-2 text-center text-xs text-gray-500 md:px-4 md:py-3 md:text-sm">
                      No notifications
                    </div>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        <div className="relative" ref={dropdownRef}>
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="flex items-center gap-2 rounded-xl px-2 py-1.5 transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500/20 md:gap-3 md:px-3 md:py-2"
            onClick={() => setDropdownOpen(open => !open)}
          >
            <div className="relative">
              <div className="flex h-7 w-7 items-center justify-center rounded-full bg-blue-600 text-xs font-semibold text-white shadow-lg md:h-8 md:w-8 md:text-sm">
                {initials}
              </div>
              <div className="absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full border-2 border-white bg-green-500 md:h-3 md:w-3"></div>
            </div>
            <div className="hidden flex-col items-start text-left md:flex">
              <span className="text-xs font-semibold text-black md:text-sm">{firstName}</span>
              <span className="text-[10px] text-gray-500 md:text-xs">{user.email}</span>
            </div>
            <motion.div animate={{ rotate: dropdownOpen ? 180 : 0 }} transition={{ duration: 0.2 }}>
              <HiChevronDown className="h-3 w-3 text-gray-400 md:h-4 md:w-4" />
            </motion.div>
          </motion.button>
          <AnimatePresence>
            {dropdownOpen && (
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: -10 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: -10 }}
                transition={{ duration: 0.15, ease: 'easeOut' }}
                className="absolute right-0 mt-2 w-56 rounded-2xl bg-white shadow-xl ring-1 ring-black/5 backdrop-blur-xl md:w-64"
              >
                <div className="border-b border-gray-100 px-3 py-3 md:px-4 md:py-4">
                  <div className="flex items-center gap-2 md:gap-3">
                    <div className="flex h-8 w-8 items-center justify-center rounded-full bg-blue-600 text-xs font-semibold text-white shadow-lg md:h-10 md:w-10 md:text-sm">
                      {initials}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-xs font-semibold text-black md:text-sm">
                        {displayName}
                      </div>
                      <div className="truncate text-[10px] text-gray-500 md:text-xs">
                        {user.email}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="py-1 md:py-2">
                  <motion.button
                    whileHover={{ backgroundColor: 'rgb(248 250 252)' }}
                    onClick={handleLogout}
                    className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs text-gray-600 transition-colors md:gap-3 md:px-4 md:py-3 md:text-sm"
                  >
                    <HiArrowRightOnRectangle className="h-3 w-3 md:h-4 md:w-4" />
                    <span>Sign out</span>
                  </motion.button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
      <LogoutConfirmation
        isOpen={showLogoutConfirm}
        onConfirm={confirmLogout}
        onCancel={() => setShowLogoutConfirm(false)}
      />
    </motion.header>
  );
};

export default AdminHeader;
